import _ from 'lodash';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';

import SimcInput from 'shared/simcinput';
import { inputInfo } from 'shared/addonInput';
import { reportNames } from 'shared/pageMetadata';

import types from 'frontend/state/actionTypes';

export function readyToSubmit(text, armoryRegion, armoryRealm, armoryName) {
  const armoryReady = armoryRegion && armoryRealm && armoryName;
  const validInput = Boolean(text);

  return Boolean(armoryReady || validInput);
}

const emailSelector = state => state.simbot.form.email;
const textSelector = state => state.simbot.form.text;
const advancedInputSelector = state => state.simbot.form.advancedInput;
const advancedDataSelector = createSelector(
  advancedInputSelector,
  input => SimcInput.parse(input),
);
const advancedActorCountSelector = createSelector(
  advancedDataSelector,
  data => SimcInput.getActors(data).length,
);
const advancedProfilesetCountSelector = createSelector(
  advancedDataSelector,
  data => SimcInput.getProfilesetNames(data).length,
);
const baseActorNameSelector = state => state.simbot.form.baseActorName;
const armoryRegionSelector = state => state.simbot.form.armoryRegion;
const armoryRealmSelector = state => state.simbot.form.armoryRealm;
const armoryNameSelector = state => state.simbot.form.armoryName;
const errorSelector = state => state.simbot.form.error;
const dataSelector = state => state.simbot.form.data;
const loadingSelector = state => state.simbot.form.loading;
const userReportNameSelector = state => state.simbot.form.reportName;
const sendEmailSelector = state => state.simbot.form.sendEmail;
const sendBrowserNotificationSelector = state => state.simbot.form.sendBrowserNotification;
const defaultBaseActorNameSelector = createSelector(
  armoryNameSelector,
  textSelector,
  (armoryName, text) => {
    let name = armoryName;
    if (text) {
      const data = SimcInput.parse(text);
      const actors = SimcInput.getActors(data);
      if (actors && actors.length > 0) {
        name = actors[0].value;
      }
    }
    return name;
  },
);
const defaultReportNameSelector = createSelector(
  (state, props) => props.type,
  (type) => {
    const defaultReportName = `${reportNames[type]}`;
    return defaultReportName;
  },
);
const reportNameSelector = createSelector(
  userReportNameSelector,
  defaultReportNameSelector,
  (userReportName, defaultReportName) => userReportName || defaultReportName,
);
const inputInfoSelector = createSelector(
  textSelector,
  inputInfo,
);
const readyToSubmitSelector = createSelector(
  textSelector,
  armoryRegionSelector,
  armoryRealmSelector,
  armoryNameSelector,
  readyToSubmit,
);

export const selectors = {
  email: emailSelector,
  reportName: reportNameSelector,
  userReportName: userReportNameSelector,
  defaultReportName: defaultReportNameSelector,

  text: textSelector,
  armoryRegion: armoryRegionSelector,
  armoryRealm: armoryRealmSelector,
  armoryName: armoryNameSelector,

  advancedInput: advancedInputSelector,
  advancedData: advancedDataSelector,

  advancedActorCount: advancedActorCountSelector,
  advancedProfilesetCount: advancedProfilesetCountSelector,

  baseActorName: baseActorNameSelector,
  defaultBaseActorName: defaultBaseActorNameSelector,

  error: errorSelector,
  data: dataSelector,
  loading: loadingSelector,

  inputInfo: inputInfoSelector,

  readyToSubmit: readyToSubmitSelector,

  sendEmail: sendEmailSelector,
  sendBrowserNotification: sendBrowserNotificationSelector,
};

const makeInputReducer = ({
  fieldName,
  initialState = '',
  inputAction = types.INPUT_FORM,
  clearActions = types.CLEAR,
  clearOnChange = [],
  trimInput = true,
}) => (state = initialState, action = {}) => {
  const { type, payload = {} } = action;
  const { field, value } = payload;
  // special case: clear this field if an clearOnChange field is updated
  if (type === inputAction && _.includes(clearOnChange, field)) {
    return '';
  }
  if (_.includes(clearActions, type)) {
    return '';
  }

  if (field !== fieldName) {
    return state;
  }
  switch (type) {
    case inputAction:
      if (trimInput) {
        return _.trim(value);
      }
      return value;
    default:
      return state;
  }
};

const reportNameReducer = (state = '', action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.INPUT_REPORT_NAME:
      return payload;
    case types.SUBMIT_SIM_SUCCESS:
      return '';
    default:
      return state;
  }
};

const formErrorReducer = (state = null, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.FORM_ERROR:
      return payload;
    case types.SUBMIT_SIM_FAILURE: {
      if (_.isString(payload)) {
        return {
          type: payload,
        };
      }
      return payload;
    }

    // generic

    case types.INPUT_FORM:
    case types.INPUT_ITEM:
    case types.SUBMIT_SIM_SUCCESS:
    case types.SUBMIT_SIM_REQUEST:
    case types.CHANGE_ROUTE:
    case types.INPUT_OPTIMIZE_TEXT:
    case types.TOGGLE_COMBINATION_ITEM:
    case types.TOGGLE_OPTIMIZE_TALENT:
    case types.RESET_OPTIMIZE_TALENTS:
    case types.SET_OPTIMIZE_TALENTS:
    case types.TOGGLE_OPTIMIZE_ENHANCEMENT:
    case types.TOGGLE_OPTIMIZE_ENHANCEMENT_REPLACE:
    case types.CLEAR_OPTIMIZE_ENHANCEMENTS:
    case types.SET_OPTIMIZE_ENHANCEMENTS_DEFAULT:
    case types.SET_OPTIMIZE_ENHANCEMENT_WRATHION_SOCKET:
    case types.CHOOSE_DROPTIMIZER_INSTANCE:
    case types.CHOOSE_DROPTIMIZER_ENCOUNTER:
    case types.CHOOSE_DROPTIMIZER_DIFFICULTY:
    case types.CHOOSE_OPTIMIZE_TALENT_LOADOUT:
      return null;
    default:
      return state;
  }
};

const dataReducer = (state = null, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.SUBMIT_SIM_SUCCESS:
      return payload;
    case types.SUBMIT_SIM_REQUEST:
    case types.SUBMIT_SIM_FAILURE:
      return null;
    default:
      return state;
  }
};

const loadingReducer = (state = false, action = {}) => {
  const { type } = action;
  switch (type) {
    case types.SUBMIT_SIM_REQUEST:
      return true;
    case types.SUBMIT_SIM_SUCCESS:
    case types.SUBMIT_SIM_FAILURE:
      return false;
    default:
      return state;
  }
};

// disable emails
const sendEmailReducer = () => {
  return false;
};

const sendBrowserNotificationReducer = (state = false, action = {}) => {
  const { type } = action;
  switch (type) {
    case types.TOGGLE_BROWSER_NOTIFICATION:
      return !state;
    default:
      return state;
  }
};

export default combineReducers({
  reportName: reportNameReducer,

  email: makeInputReducer({
    fieldName: 'email',
    inputAction: types.INPUT_FORM,
  }),
  // text and armoryName are mutually exclusive - if one is updated, clear the
  // other
  text: makeInputReducer({
    fieldName: 'text',
    clearOnChange: ['armoryName'],
    inputAction: types.INPUT_FORM,
    trimInput: false,
  }),
  armoryRegion: makeInputReducer({
    fieldName: 'armoryRegion',
    inputAction: types.INPUT_FORM,
    initialState: 'us',
  }),
  armoryRealm: makeInputReducer({
    fieldName: 'armoryRealm',
    clearOnChange: ['armoryRegion'],
    inputAction: types.INPUT_FORM,
  }),
  armoryName: makeInputReducer({
    fieldName: 'armoryName',
    clearOnChange: ['text'],
    inputAction: types.INPUT_FORM,
  }),
  advancedInput: makeInputReducer({
    fieldName: 'advancedInput',
    inputAction: types.INPUT_FORM,
    trimInput: false,
  }),
  baseActorName: makeInputReducer({
    fieldName: 'baseActorName',
    inputAction: types.INPUT_FORM,
    clearActions: [types.SUBMIT_SIM_SUCCESS],
    trimInput: false,
  }),

  error: formErrorReducer,
  data: dataReducer,
  loading: loadingReducer,
  sendEmail: sendEmailReducer,
  sendBrowserNotification: sendBrowserNotificationReducer,
});
